import React, { useState, useEffect } from 'react';
import { X, User, Phone, CheckCircle2 } from 'lucide-react';
import { UserProfile, LoginHistory } from '../types';
import { formatPhoneNumber } from '../utils/formatters';
import { updateProfile } from '../api/auth';
import { getLoginHistory } from '../api/history';
import { Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

interface MyInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: UserProfile;
  onProfileUpdate: (updatedProfile: UserProfile) => void;
}

const MyInfoModal: React.FC<MyInfoModalProps> = ({
  isOpen,
  onClose,
  profile,
  onProfileUpdate,
}) => {
  const [name, setName] = useState(profile.name || '');
  const [phone, setPhone] = useState(profile.phone || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(profile.name || '');
      setPhone(profile.phone || '');
      setShowSuccess(false);
      fetchHistory();
    }
  }, [isOpen, profile]);

  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const history = await getLoginHistory();
      setLoginHistory(history);
    } catch (error) {
      console.error('Failed to fetch login history', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      alert('성함을 입력해주세요.');
      return;
    }
    if (!phone.trim()) {
      alert('전화번호를 입력해주세요.');
      return;
    }

    setIsSaving(true);
    try {
      const updated = await updateProfile({ name, phone });
      onProfileUpdate({
        ...profile,
        name: updated.name,
        phone: updated.phone
      });

      setShowSuccess(true);
      alert('성공적으로 저장되었습니다.');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to update profile', error);
      alert(
        error instanceof Error
          ? error.message
          : '정보 수정 중 오류가 발생했습니다.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="relative w-full max-w-lg bg-white rounded-t-[32px] sm:rounded-[32px] shadow-2xl overflow-y-auto max-h-[92dvh] overscroll-contain animate-in slide-in-from-bottom duration-300 custom-scrollbar">
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors shadow-sm"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-8 pb-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
              <User size={24} />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900">My 정보 수정</h2>
              <p className="text-[13px] font-bold text-gray-400">배송 및 주문 확인을 위한 정보입니다.</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="relative group scroll-mt-32">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider mb-2 block px-1">고객명 <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <User size={18} />
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="성함을 입력해주세요"
                  className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl pl-12 pr-4 text-[15px] font-bold focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all placeholder:text-gray-300"
                />
              </div>
            </div>

            <div className="relative scroll-mt-32">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider mb-2 block px-1">전화번호 <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <Phone size={18} />
                </span>
                <input
                  type="tel"
                  value={formatPhoneNumber(phone)}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="010-0000-0000"
                  className="w-full h-14 bg-gray-50 border border-gray-100 rounded-2xl pl-12 pr-4 text-[15px] font-bold focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all placeholder:text-gray-300"
                />
              </div>
            </div>
          </div>

          <div className="mt-10">
            <button
              onClick={handleSave}
              disabled={isSaving || showSuccess}
              className={`w-full h-16 rounded-2xl font-black text-lg shadow-lg transition-all flex items-center justify-center gap-2 active:scale-[0.98] ${showSuccess
                ? 'bg-emerald-50 text-emerald-600 shadow-none'
                : isSaving
                  ? 'bg-gray-100 text-gray-400 shadow-none'
                  : 'bg-emerald-500 text-white shadow-emerald-100 hover:bg-emerald-600'
                }`}
            >
              {showSuccess ? (
                <>
                  <CheckCircle2 size={24} />
                  저장 완료!
                </>
              ) : isSaving ? (
                <div className="w-6 h-6 border-3 border-gray-300 border-t-emerald-500 rounded-full animate-spin" />
              ) : '저장하기'}
            </button>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400">
                <Clock size={16} />
              </div>
              <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">최근 로그인 이력</h3>
            </div>

            <div className="space-y-3 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
              {isLoadingHistory ? (
                <div className="text-center py-4 text-xs text-gray-400">이력 불러오는 중...</div>
              ) : loginHistory.length > 0 ? (
                loginHistory.map((log) => (
                  <div key={log.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100/50">
                    <div className="flex flex-col gap-1">
                      <span className="text-[13px] font-bold text-gray-900">
                        {format(new Date(log.createdAt), 'yyyy년 MM월 dd일 HH:mm', { locale: ko })}
                      </span>
                      <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full w-fit">
                        정상 로그인
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-[11px] font-bold text-gray-400 block">IP: {log.ipAddress || '알 수 없음'}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10 text-xs text-gray-400 italic bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                  로그인 이력이 없습니다.
                </div>
              )}
            </div>
          </div>
          <div className="h-10 sm:h-0" />
        </div>
      </div>
    </div>
  );
};

export default MyInfoModal;
